import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, Input, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import AgoraRTC, { IAgoraRTCRemoteUser } from 'agora-rtc-sdk-ng';

import { Store } from '@ngrx/store';

import { selectIsFullScreenVideoCall, selectUser, selectVideoCall } from '../../../../store/app.selectors';

import { MessagingEnvironmentService } from '../../../../core/services/messaging-environment.service';
import { SnackBarService } from '../../../../core/services/snackbar.service';
import { LoggerService } from '../../../../core/services/logger.service';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { setIsFullScreenVideoCall } from '../../../shared/store/ui.actions';
import { IMessageableEntity } from '../../interfaces/IMessageableEntity';
import { IAgoraRTC, IVideoCallMember } from '../../interfaces/IVideoCallMember';
import { DenoiserService } from '../../services/denoise.service';
import { MessagingApiService } from '../../services/messaging-api/messaging-api.service';
import { SoundsService } from '../../services/sounds.service';
import { WebsocketService } from '../../services/websocket.service';
import { setVideoCall } from '../../store/chat.actions';
import { AppStateWithChat } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-video-conference, ngx-messaging-video-conference',
  templateUrl: './video-conference.component.html',
  styleUrls: ['./video-conference.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VideoConferenceComponent extends BaseComponent implements OnInit {
  @Input() expand: boolean;
  @Input() isVideoCallView: boolean;
  audio = true;
  video = true;
  isLoadingCall = true;
  isLoadingSettings = true;
  time = 0;
  display: string;
  interval: NodeJS.Timeout;
  rtc: IAgoraRTC = {
    client: null,
    localAudioTrack: null,
    localVideoTrack: null,
  };
  options = {
    appId: '',
    channel: '',
    token: '',
  };
  members: IVideoCallMember[] = [];
  isEmbededView: boolean;
  callEnd: boolean;
  elementVideoLoading: IVideoCallMember;
  reloadCamera: boolean;
  reloadAudio: boolean;
  videoTrack: MediaStreamTrack;
  audioTrack: MediaStreamTrack;

  private messagingService = inject(MessagingApiService);
  private store = inject(Store<AppStateWithChat>);
  private denoiserService = inject(DenoiserService);
  private chatService = inject(WebsocketService);
  private soundsService = inject(SoundsService);
  private snackBarService = inject(SnackBarService);
  private env = inject(MessagingEnvironmentService);
  private logger = inject(LoggerService);
  private cdr = inject(ChangeDetectorRef);

  videoCall = toSignal(this.store.select(selectVideoCall), { initialValue: null });
  user = toSignal(this.store.select(selectUser), { initialValue: null });
  isFullScreenVideoCall = toSignal(this.store.select(selectIsFullScreenVideoCall), { initialValue: false });

  constructor() {
    super();
    effect(() => {
      const videoCall = this.videoCall();
      if (videoCall) {
        let validString = videoCall.room.replace(/[-_]/g, '');
        if (validString?.length > 32) {
          validString = validString.slice(0, 32);
        }
        this.options.channel = validString;
      } else {
        this.leaveCall();
      }
    });
  }

  async ngOnInit() {
    this.options.appId = this.env.env.agoraAppID;
    this.loadNavigatorPermissions();
  }

  loadNavigatorPermissions(): void {
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: true })
      .then(async (res) => {
        this.videoTrack = res.getVideoTracks()[0];
        this.audioTrack = res.getAudioTracks()[0];
        await this.loadRTCVideo();
        await this.loadRTCAudio();
        this.messagingService.getTokenVideoCall(this.options.channel).subscribe(
          (data) => {
            this.options.token = data.token;
            this.join();
            this.cdr.markForCheck();
          },
          (err) => {
            this.logger.error(err, 'VideoConference.getTokenVideoCall');
            this.snackBarService.showErrorSnackBar('Error geting token');
            this.leaveCall();
          }
        );
      })
      .catch(() => {
        this.snackBarService.showErrorSnackBar('Please allow permissions to use video calls');
        this.leaveCall();
        this.store.dispatch(setVideoCall({ videoCall: null }));
      });
  }

  startTimeout() {
    setTimeout(() => {
      if (this.isLoadingCall) {
        this.leaveCall();
      }
    }, 40000);
  }

  async loadRTCVideo() {
    this.reloadCamera = false;
    try {
      this.rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
      if (this.members.length) {
        this.members[0].remoteVideoPlayer = this.rtc.localVideoTrack;
      }
    } catch (error) {
      this.reloadCamera = true;
      this.logger.error(error, 'VideoConference.loadRTCVideo');
    }
  }

  async loadRTCAudio() {
    this.reloadAudio = false;
    try {
      this.rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      // this.rtc.localAudioTrack.pipe(this.denoiserService.processor).pipe(this.rtc.localAudioTrack.processorDestination);
      // await this.denoiserService.processor.enable();
      // this.denoiserService.processor.onoverload = async () => {
      //   await this.denoiserService.processor.disable();
      // };
      if (this.members.length) {
        this.members[0].remoteAudioPlayer = this.rtc.localAudioTrack;
        this.members[0].remoteAudioPlayer.play();
      }
    } catch (error) {
      this.reloadAudio = true;
      this.logger.error(error, 'VideoConference.loadRTCAudio');
    }
  }

  loadSound() {
    if (this.videoCall()?.type === 0) {
      // this.soundsService.ringtone.play();
      // this.soundsService.ringtone.addEventListener(
      //   'ended',
      //   function () {
      //     this.currentTime = 0;
      //     this.play();
      //   },
      //   false,
      // );
    }
  }

  loadClient() {
    if (!this.rtc.client || !this.rtc.localVideoTrack || !this.rtc.localAudioTrack) {
      return;
    }
    this.members.push({
      picName: this.user()?.subjectMessageableEntity?.displayName ?? '',
      displayName: 'You',
      agoraUid: this.rtc.client.uid?.toString() ?? '',
      audio: true,
      video: true,
      containerVideo: null,
      remoteVideoPlayer: this.rtc.localVideoTrack,
      remoteAudioPlayer: this.rtc.localAudioTrack,
      uid: null,
      memberUid: this.user()?.subjectMessageableEntity?.uid ?? null,
      imageURL: this.user()?.subjectMessageableEntity?.profileImageURL,
    });
    this.rtc.client.publish([this.rtc.localAudioTrack, this.rtc.localVideoTrack]);
    this.setWebsocket(this.options.channel);
    this.messagingService.joinCallRoom(this.options.channel, this.rtc.client.uid?.toString() ?? '').subscribe((data) => {
      this.setParticipants(data.participants);
    });
  }

  async join() {
    this.startTimer();
    this.rtc.client = await AgoraRTC.createClient({ mode: 'rtc', codec: 'h264' });
    this.logger.debug('Joining video call', { channel: this.options.channel });
    this.rtc.client
      .join(this.options.appId, this.options.channel, this.options.token, null)
      .then(() => {
        this.isLoadingSettings = false;
        this.loadSound();
        this.subscribeUserPublished();
        this.subscribeUserUnPublished();
        this.subscribeUserLeft();
        this.loadClient();
        this.startTimeout();
        this.cdr.markForCheck();
      })
      .catch((err) => {
        this.logger.error(err, 'VideoConference.join');
        this.snackBarService.showErrorSnackBar('Error joining to call');
        this.leaveCall();
      });
  }

  subscribeUserPublished(): void {
    if (!this.rtc.client) {
      return;
    }
    this.rtc.client.on('user-published', async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      if (!this.rtc.client) {
        return;
      }
      await this.rtc.client.subscribe(user, mediaType);
      let index = this.members.map((e) => e.agoraUid).indexOf(user.uid.toString());
      if (index === -1) {
        this.members.push({
          displayName: 'Loading...',
          agoraUid: user.uid.toString(),
          remoteVideoPlayer: null,
          remoteAudioPlayer: null,
          audio: false,
          video: false,
          uid: null,
          containerVideo: null,
          picName: 'Loading...',
          memberUid: null,
        });
        index = this.members.length - 1;
        // this.soundsService.joinSound.play();
      }
      if (mediaType === 'video') {
        this.members[index].remoteVideoPlayer = user.videoTrack ?? null;
        this.setVideo(true, user.uid.toString(), false);
      }
      if (mediaType === 'audio') {
        this.members[index].remoteAudioPlayer = user.audioTrack ?? null;
        this.setAudio(true, user.uid.toString(), false);
      }
      if (this.members.length > 1) {
        this.isLoadingCall = false;
        // this.soundsService.ringtone.pause();
      }
      this.cdr.markForCheck();
    });
  }

  subscribeUserUnPublished(): void {
    if (!this.rtc.client) {
      return;
    }
    this.rtc.client.on('user-unpublished', (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      if (mediaType === 'video') {
        this.setVideo(false, user.uid.toString(), false);
      }
      if (mediaType === 'audio') {
        this.setAudio(false, user.uid.toString(), false);
      }
    });
  }

  subscribeUserLeft(): void {
    if (!this.rtc.client) {
      return;
    }
    this.rtc.client.on('user-left', (user: IAgoraRTCRemoteUser) => {
      // this.soundsService.leftSound.play();
      this.deleteParticipant(user.uid.toString());
    });
  }

  setWebsocket(uid: string) {
    this.chatService.messagingWs.watch(`/messaging/call/room/${uid}`).subscribe((message: { body: string }) => {
      if (message.body) {
        const body = JSON.parse(message.body) as string;
        const bodyJson = JSON.parse(body) as {
          room: { participants: { uid: string; messageableEntity: IMessageableEntity }[] };
        };
        this.setParticipants(bodyJson.room.participants);
      }
    });
  }

  setParticipants(participants: { uid: string; messageableEntity: IMessageableEntity }[]) {
    for (const member of this.members) {
      const participant = participants.find((e) => e.uid?.toString() === member?.agoraUid?.toString());
      if (participant) {
        if (participant?.uid?.toString() !== this.rtc?.client?.uid?.toString()) {
          member.displayName = participant.messageableEntity.displayName;
          member.picName = participant.messageableEntity.displayName;
          member.memberUid = participant.messageableEntity.uid;
        }
      }
    }
    this.deleteMembersDuplicated();
  }
  deleteMembersDuplicated() {
    const newArray: IVideoCallMember[] = [];
    const memberSelected: { [key: string]: IVideoCallMember } = {};
    for (const i in this.members) {
      memberSelected[this.members[i].memberUid ?? ''] = this.members[i];
    }
    for (const member in memberSelected) {
      newArray.push(memberSelected[member]);
    }
    this.members = newArray;
  }

  deleteParticipant(uid: string) {
    const index = this.members.map((e) => e.agoraUid).indexOf(uid.toString());
    if (index !== -1) {
      this.members.splice(index, 1);
    }
  }

  async leaveCall() {
    this.pauseTimer();
    // this.soundsService.ringtone.pause();
    if (this.rtc?.localVideoTrack) {
      this.rtc.localVideoTrack.setEnabled(false);
    }
    if (this.rtc?.localAudioTrack) {
      this.rtc.localAudioTrack.setEnabled(false);
    }
    this.videoTrack.stop();
    this.audioTrack.stop();
    if (this.rtc?.client?.remoteUsers) {
      this.members.forEach(async (member: IVideoCallMember) => {
        const playerContainer = document.getElementById(member.agoraUid.toString());
        playerContainer && playerContainer.remove();
      });
    }
    if (this.rtc?.client?.uid) {
      this.messagingService.leaveCallRoom(this.options.channel, this.rtc?.client?.uid?.toString()).subscribe();
      await this.rtc.client.leave();
    }
    this.members = [];
    this.callEnd = true;
    if (!this.isVideoCallView) {
      setTimeout(() => {
        this.store.dispatch(setVideoCall({ videoCall: null }));
      }, 1000);
      this.store.dispatch(setIsFullScreenVideoCall({ value: false }));
    }
  }

  setVideo(value: boolean, agoraUid: string | number, local: boolean) {
    const index = this.members.map((e) => e.agoraUid).indexOf(agoraUid.toString());
    if (index !== -1) {
      this.members[index].video = value;
      if (local && this.rtc.localVideoTrack) {
        this.rtc.localVideoTrack.setEnabled(value);
        this.video = value;
      }
      if (value && this.members[index].remoteVideoPlayer && this.members[index].containerVideo) {
        this.members[index].remoteVideoPlayer.play(this.members[index].containerVideo);
      }
    }
  }

  setAudio(value: boolean, agoraUid: string | number, local: boolean) {
    const index = this.members.map((e) => e.agoraUid).indexOf(agoraUid.toString());
    if (index !== -1) {
      this.members[index].audio = value;
      if (local && this.rtc.localAudioTrack) {
        this.rtc.localAudioTrack.setEnabled(value);
        this.audio = value;
      } else {
        if (value && this.members[index].remoteAudioPlayer) {
          this.members[index].remoteAudioPlayer.play();
        }
      }
    }
  }

  async setDenoiser() {
    if (this.denoiserService.processor.enabled) {
      await this.denoiserService.processor.disable();
    } else {
      await this.denoiserService.processor.enable();
    }
  }

  startTimer() {
    if (!this.time) {
      this.interval = setInterval(() => {
        this.time++;
        this.display = this.transform(this.time);
      }, 1000);
    }
  }

  transform(value: number): string {
    const minutes: number = Math.floor(value / 60);
    return ('0' + minutes).slice(-2) + ':' + ('0' + (value - minutes * 60)).slice(-2);
  }
  pauseTimer() {
    clearInterval(this.interval);
  }

  calcVideoWidth(): string {
    return '100%';
  }

  calcVideoHeight(): string {
    return 'auto';
  }

  expandView() {
    this.store.dispatch(setIsFullScreenVideoCall({ value: !this.isFullScreenVideoCall() }));
  }
}
